/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import Event from "../lib/Event";
//import FirebaseUtils from "lib/FirebaseUtils";
import PubSub from "../lib/PubSub";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import _ from "underscore";
import {
  IonCard,
  IonCardContent,
  IonGrid,
  IonCol,
  IonRow,
  IonButton,
  IonIcon,
  IonList,
  IonListHeader,
  IonLabel,
  IonItem,
  IonInput,
  IonSkeletonText,
} from "@ionic/react";

import glutenFree from "../assets/img/icons/gluten_free.svg";
import { add, remove } from "ionicons/icons";
import StringUtils from "../lib/StringUtils";
import SegmentUtils from "../lib/SegmentUtils";

const BillingAddressForm: React.FC<{
  user?: any;
  onChange?: any;
  title?: any;
  description?: any;
  loading?: boolean;
  autoFill?: string;
}> = ({
  user = null,
  onChange = null,
  title = "Enter Your Address",
  description = null,
  loading = false,
  autoFill = "",
}) => {
  const addressRef: any = useRef<HTMLIonInputElement>(null);
  const [name, setName] = useState<string>("");
  const [address1, setAddress1] = useState<string>("");
  const [address2, setAddress2] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");

  const win: any = window;

  useEffect(() => {
    setTimeout(() => {
      setupAutocomplete(addressRef?.current);
    }, 10);
  }, [addressRef?.current]);

  useEffect(() => {
    if (typeof onChange == "function") {
      onChange({
        name,
        address1,
        address2,
        city,
        state,
        zip: zipCode,
      });
    }
  }, [name, address1, address2, city, state, zipCode]);

  useEffect(() => {
    if (user && autoFill && user?.hasOwnProperty(autoFill)) {
      const address = user?.[autoFill];

      //console.log("ADDRESS", address);

      setName(user?.name ? user?.name : "");
      setAddress1(address?.address1 ? address?.address1 : "");
      setAddress2(address?.address2 ? address?.address2 : "");
      setCity(address?.city ? address?.city : "");
      setState(address?.state ? address?.state : "");
      setZipCode(address?.zip ? address?.zip : "");
    }
  }, [user, autoFill]);

  /**
   * Sets up the autocomplete component
   */
  const setupAutocomplete = useCallback(
    async (input: any) => {
      // Google Places API auto complete

      if (!input) {
        console.error("Address input not found");

        return;
      }

      if (!win?.google?.maps) {
        console.error("Maps not loaded");

        return;
      }

      const element = input;

      //console.log("Maps loaded");

      let autocomplete = new win.google.maps.places.Autocomplete(element, {
        types: ["geocode"],
      });
      google.maps.event.addListener(autocomplete, "place_changed", (e: any) => {
        // retrieve the place object for your use
        let place = autocomplete.getPlace();

        if (!place?.address_components?.length) {
          setAddress1(place?.name);

          return;
        }

        //console.log("PLACE CHANGED", place);

        const streetNumber = _.find(place?.address_components, (item: any) => {
          return item?.types?.includes("street_number");
        });

        const route = _.find(place?.address_components, (item: any) => {
          return item?.types?.includes("route");
        });

        let locality = _.find(place?.address_components, (item: any) => {
          return item?.types?.includes("locality");
        });

        if (!locality) {
          locality = _.find(place?.address_components, (item: any) => {
            return item?.types?.includes("neighborhood");
          });
        }

        if (!locality) {
          locality = _.find(place?.address_components, (item: any) => {
            return item?.types?.includes("administrative_area_level_3");
          });
        }

        const administrativeAreaLevel1 = _.find(
          place?.address_components,
          (item: any) => {
            return item?.types?.includes("administrative_area_level_1");
          },
        );

        const postalCode = _.find(place?.address_components, (item: any) => {
          return item?.types?.includes("postal_code");
        });

        setTimeout(() => {
          let address1 = `${
            streetNumber?.long_name ? streetNumber?.long_name : ""
          } ${route?.long_name ? route?.long_name : ""}`;

          //console.log("SET ADDRESS", address1);

          if (addressRef?.current) {
            addressRef.current.value = address1;
          }

          setAddress1(address1);
          setCity(locality?.long_name);
          setAddress2("");
          setState(administrativeAreaLevel1?.long_name);
          setZipCode(postalCode?.long_name);
        }, 1);
      });
    },
    [win?.google?.maps],
  );

  return (
    <>
      <div className="py-3">
        <div className="mb-4 text-center">
          <h3 className="font-medium text-xl font-[SourceSerif]">
            {title ? title : "Enter Your Address"}
          </h3>
          {description ? <p className="">{description}</p> : null}
        </div>

        <div className="relative mb-4">
          {loading ? (
            <IonSkeletonText
              className="rounded h-[50px] w-full"
              animated={true}
            ></IonSkeletonText>
          ) : (
            <>
              <label
                htmlFor="address1"
                className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs sm:text-sm font-medium text-gray-900"
              >
                Street Address
              </label>
              <input
                ref={addressRef}
                autoComplete="address-line1"
                type="text"
                name="address1"
                id="address1"
                className="block w-full outline-0 rounded border-[1px] px-[0.75rem] py-[0.75rem] sm:px-3 sm:py-3 text-gray-900 shadow-sm ring-0 placeholder:text-gray-400 text-sm sm:text-base sm:leading-6"
                placeholder="Street Address"
                value={address1}
                required={true}
                onChange={(event: any) => {
                  //console.log("ADDRESS CHANGED", event?.target?.value);

                  setAddress1(event?.target?.value);
                }}
              />
            </>
          )}
        </div>
        <div className="relative mb-4">
          {loading ? (
            <IonSkeletonText
              className="rounded h-[50px] w-full"
              animated={true}
            ></IonSkeletonText>
          ) : (
            <>
              <label
                htmlFor="address2"
                className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs sm:text-sm font-medium text-gray-900"
              >
                APT/Suite #
              </label>
              <input
                autoComplete="address-line2"
                type="text"
                name="address2"
                id="address2"
                className="block w-full outline-0 rounded border-[1px] px-[0.75rem] py-[0.75rem] sm:px-3 sm:py-3 text-gray-900 shadow-sm ring-0 placeholder:text-gray-400 text-sm sm:text-base sm:leading-6"
                placeholder="APT/Suite #"
                value={address2}
                required={true}
                onChange={(event: any) => {
                  setAddress2(event?.target?.value);
                }}
              />
            </>
          )}
        </div>
        <div className="relative mb-4">
          {loading ? (
            <IonSkeletonText
              className="rounded h-[50px] w-full"
              animated={true}
            ></IonSkeletonText>
          ) : (
            <>
              <label
                htmlFor="city"
                className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs sm:text-sm font-medium text-gray-900"
              >
                City
              </label>
              <input
                autoComplete="address-level2"
                type="text"
                name="city"
                id="city"
                className="block w-full outline-0 rounded border-[1px] px-[0.75rem] py-[0.75rem] sm:px-3 sm:py-3 text-gray-900 shadow-sm ring-0 placeholder:text-gray-400 text-sm sm:text-base sm:leading-6"
                placeholder="City"
                value={city}
                required={true}
                onChange={(event: any) => {
                  setCity(event?.target?.value);
                }}
              />
            </>
          )}
        </div>
        <div className="relative mb-4">
          {loading ? (
            <IonSkeletonText
              className="rounded h-[50px] w-full"
              animated={true}
            ></IonSkeletonText>
          ) : (
            <>
              <label
                htmlFor="state"
                className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs sm:text-sm font-medium text-gray-900"
              >
                State
              </label>
              <select
                style={
                  {
                    "-webkit-appearance": "none",
                    "-moz-appearance": "none",
                    "-ms-appearance": "none",
                    "-o-appearance": "none",
                    appearance: "none",
                  } as any
                }
                name="state"
                id="state"
                className="block w-full bg-white outline-0 rounded border-[1px] px-[0.75rem] py-[0.75rem] sm:px-3 sm:py-3 text-gray-900 shadow-sm ring-0 placeholder:text-gray-400 text-sm sm:text-base sm:leading-6"
                placeholder="State"
                value={state}
                required={true}
                onChange={(event: any) => {
                  setState(event?.target?.value);
                }}
              >
                <optgroup>
                  <option value="" disabled>Select Your State</option>
                  <option value="Alabama">Alabama</option>
                  <option value="Alaska">Alaska</option>
                  <option value="Arizona">Arizona</option>
                  <option value="Arkansas">Arkansas</option>
                  <option value="California">California</option>
                  <option value="Colorado">Colorado</option>
                  <option value="Connecticut">Connecticut</option>
                  <option value="Delaware">Delaware</option>
                  <option value="District Of Columbia">
                    District Of Columbia
                  </option>
                  <option value="Florida">Florida</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Hawaii">Hawaii</option>
                  <option value="Idaho">Idaho</option>
                  <option value="Illinois">Illinois</option>
                  <option value="Indiana">Indiana</option>
                  <option value="Iowa">Iowa</option>
                  <option value="Kansas">Kansas</option>
                  <option value="Kentucky">Kentucky</option>
                  <option value="Louisiana">Louisiana</option>
                  <option value="Maine">Maine</option>
                  <option value="Maryland">Maryland</option>
                  <option value="Massachusetts">Massachusetts</option>
                  <option value="Michigan">Michigan</option>
                  <option value="Minnesota">Minnesota</option>
                  <option value="Mississippi">Mississippi</option>
                  <option value="Missouri">Missouri</option>
                  <option value="Montana">Montana</option>
                  <option value="Nebraska">Nebraska</option>
                  <option value="Nevada">Nevada</option>
                  <option value="New Hampshire">New Hampshire</option>
                  <option value="New Jersey">New Jersey</option>
                  <option value="New Mexico">New Mexico</option>
                  <option value="New York">New York</option>
                  <option value="North Carolina">North Carolina</option>
                  <option value="North Dakota">North Dakota</option>
                  <option value="Ohio">Ohio</option>
                  <option value="Oklahoma">Oklahoma</option>
                  <option value="Oregon">Oregon</option>
                  <option value="Pennsylvania">Pennsylvania</option>
                  <option value="Rhode Island">Rhode Island</option>
                  <option value="South Carolina">South Carolina</option>
                  <option value="South Dakota">South Dakota</option>
                  <option value="Tennessee">Tennessee</option>
                  <option value="Texas">Texas</option>
                  <option value="Utah">Utah</option>
                  <option value="Vermont">Vermont</option>
                  <option value="Virginia">Virginia</option>
                  <option value="Washington">Washington</option>
                  <option value="West Virginia">West Virginia</option>
                  <option value="Wisconsin">Wisconsin</option>
                  <option value="Wyoming">Wyoming</option>
                </optgroup>
                <optgroup label="--------------------">
                  <option value="American Samoa">American Samoa</option>
                  <option value="Guam">Guam</option>
                  <option value="Northern Mariana Islands">
                    Northern Mariana Islands
                  </option>
                  <option value="Puerto Rico">Puerto Rico</option>
                  <option value="United States Minor Outlying Islands">
                    United States Minor Outlying Islands
                  </option>
                  <option value="VI">Virgin Islands</option>
                </optgroup>
              </select>
            </>
          )}
        </div>
        <div className="relative mb-4">
          {loading ? (
            <IonSkeletonText
              className="rounded h-[50px] w-full"
              animated={true}
            ></IonSkeletonText>
          ) : (
            <>
              <label
                htmlFor="zipCode"
                className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs sm:text-sm font-medium text-gray-900"
              >
                Postal Code
              </label>
              <input
                autoComplete="postal-code"
                type="number"
                name="zipCode"
                id="zipCode"
                className="block w-full outline-0 rounded border-[1px] px-[0.75rem] py-[0.75rem] sm:px-3 sm:py-3 text-gray-900 shadow-sm ring-0 placeholder:text-gray-400 text-sm sm:text-base sm:leading-6"
                placeholder="Postal Code"
                value={zipCode}
                required={true}
                onChange={(event: any) => {
                  setZipCode(event?.target?.value);
                }}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BillingAddressForm;
